import { useSWRMutation } from '@tiger/swr';
import { Button, Form } from 'antd';
import _ from 'lodash';
import React, { FC } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { BankCardSourceType } from 'components/BankCardSelect/type';
import Debug from 'components/Debug';
import ErrorBox from 'components/ErrorBox';
import FormLayout from 'components/FormLayout';
import Loading from 'components/Loading';
import { useConfirmModal } from 'components/Modal';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import usePageSafeMode from 'hooks/usePageSafeMode';
import BankCardForm, { BankCardFormItemType } from 'modules/BankCards/components/BankCardForm';
import security from 'stores/security';
import API from 'utils/API';
import http from 'utils/http';
import { useDeleteBankCard, useGetBankCardItem } from '../../request';
import './style.scss';

export interface EditLocationStates {
    next?: string;
    source: BankCardSourceType;
}

interface EditProps {
    $formutil: $Formutil<BankCardFormItemType>;
}

const Edit: FC<EditProps> = ({ $formutil }) => {
    usePageSafeMode();

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const locationStates = location.state as EditLocationStates | null;
    const { source, next } = locationStates || {};
    const { $invalid, $batchState, $getFirstError, $params } = $formutil;
    const { isLoading, error, mutate, data } = useGetBankCardItem(id!);
    const { open: openConfirmModal } = useConfirmModal();
    const { trigger } = useDeleteBankCard();
    const { trigger: editBankCardTrigger } = useSWRMutation('bankcards.edit', (params: BankCardFormItemType) =>
        http.post(
            API.TRADE.bankcards.editBankCardItem(id!),
            {
                ...params,
                bankAccountType: 1, // 美国用户的银行账号基本都是单币种类型
                supportCurrencies: 'USD', // 单币种传入美元
                source,
                accountId: security.currentAccountId,
                customerId: security.currentCustomerId
            },
            {
                params: {
                    customer_id: security.currentCustomerId,
                    account_id: security.currentAccountId
                }
            }
        )
    );

    const onSubmit = async () => {
        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            Toast.show($getFirstError());
        } else {
            Toast.loading(true);

            try {
                await editBankCardTrigger($params);
                await mutate();

                navigate(-1);
            } catch (err) {
                Toast.show(err);
            }

            Toast.loading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleDelete = async () => {
        openConfirmModal({
            content: <p className="text-center">{__('Are you sure you would like to delete this wire profile?')}</p>,
            okText: __('Delete'),
            className: 'wire-delete-card-modal',
            onOk: async () => {
                try {
                    await trigger(id!);

                    navigate(-1);
                } catch (err) {
                    Toast.show(err);
                }
            }
        });
    };

    if (!source) {
        return <Navigate to="/profile" replace />;
    }

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <ErrorBox error={error} onClick={() => mutate()} />;
    }

    if (!data?.id) {
        return <Navigate to={next || '/profile'} replace />;
    }

    return (
        <div className="bankcard-edit-root">
            <ScrollTop />
            <h3 className="main-title">{__('Edit bank account')}</h3>
            <Form layout="vertical" noValidate onFinish={onSubmit}>
                <BankCardForm bankInfo={data} source={source} />
                <FormLayout layout="1:1:2" className="btn-box">
                    <Button className="cancel btn-important" onClick={handleCancel}>
                        {__('Cancel')}
                    </Button>
                    <Button className="delete btn-important" danger onClick={handleDelete}>
                        {__('Delete')}
                    </Button>
                    <Button className="save btn-important" type="primary" htmlType="submit">
                        {__('Save')}
                    </Button>
                </FormLayout>
            </Form>
            <Debug data={$params} />
        </div>
    );
};

export default withForm(Edit);
